import { memo, useMemo, type ComponentPropsWithoutRef } from "react";
import type { Hint } from "~/features/chat/types";
import type { TermMatcher } from "~/utils/termMatcher";
import { buildTermRegex } from "~/utils/termMatching";
import { MdastMarkdown } from "./MdastMarkdown";

type HighlightedMdastMarkdownProps = Omit<
  ComponentPropsWithoutRef<typeof MdastMarkdown>,
  "termHighlight"
> & {
  terms: Hint[];
  medicationMatcher?: TermMatcher | null;
};

/**
 * MDAST renderer with medical/AI term highlighting — the v2 counterpart of
 * `HighlightedMarkdown` (§6.1/§6.3). It builds the shared AI-term regex and
 * hands it plus the medication matcher to `MdastMarkdown`'s single HAST
 * highlight pass, so highlighting uses the exact same match/priority logic as
 * v1 and opens the same `TermDefinitionDialog`.
 */
export const HighlightedMdastMarkdown = memo(
  ({ terms, medicationMatcher, ...rest }: HighlightedMdastMarkdownProps) => {
    const regex = useMemo(() => buildTermRegex(terms), [terms]);
    const matcher = medicationMatcher ?? null;
    const termHighlight = useMemo(() => ({ regex, matcher }), [regex, matcher]);

    return <MdastMarkdown {...rest} termHighlight={termHighlight} />;
  }
);

HighlightedMdastMarkdown.displayName = "HighlightedMdastMarkdown";
